import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, Link2, Users, UserCheck, Wallet, BarChart3, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function CopyButton({ text, testId, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };
  return (
    <button
      onClick={handleCopy}
      data-testid={testId}
      className="btn-outline-gold px-3 py-1.5 text-xs flex items-center gap-1.5 shrink-0"
    >
      {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : label}
    </button>
  );
}

export default function IBDashboard() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [wallet, setWallet] = useState(null);

  useEffect(() => {
    api.get("/ib/profile").then((res) => setProfile(res.data)).catch(() => {});
    api.get("/ib/wallet").then((res) => setWallet(res.data)).catch(() => setWallet(null));
  }, []);

  if (!profile) {
    return (
      <div className="space-y-8 animate-pulse" data-testid="ib-dashboard-loading">
        <div className="card-lux h-40" />
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-lux h-56" />
          <div className="card-lux h-56" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="ib-dashboard">
      <div className="card-lux relative overflow-hidden p-8 sm:p-10">
        <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <p className="eyebrow-label mb-3">Introducing Broker Portal</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white" data-testid="ib-dashboard-welcome-heading">
          Welcome, {user?.full_name?.split(" ")[0]}
        </h1>
        <p className="mt-3 text-sm text-[#94A3B8] max-w-2xl leading-relaxed">
          Share your referral link to grow your network. Every client who registers with
          your code is attributed to your portfolio.
        </p>
      </div>

      {/* ============ DESKTOP (>=768px) — Topics + My Activity, compact Referral, then stats ============ */}
      <div className="hidden md:block space-y-8" data-testid="ib-dashboard-desktop">
        {/* Row A: Topics (primary broadcast quick-access, stronger gold) + My Activity */}
        <div className="grid grid-cols-2 gap-6">
          <Link
            to="/ib/topics"
            data-testid="ib-topics-link"
            className="card-lux card-lux-hover relative overflow-hidden p-8 flex flex-col justify-between border-[#D4AF37] shadow-[0_0_50px_rgba(212,175,55,0.22)] group min-h-[240px]"
          >
            <div className="absolute -bottom-16 -right-10 h-56 w-56 rounded-full bg-[#D4AF37]/20 blur-3xl pointer-events-none" aria-hidden="true" />
            <div className="relative flex items-start justify-between">
              <span className="h-14 w-14 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/50 flex items-center justify-center">
                <Send className="h-6 w-6 text-[#D4AF37]" />
              </span>
              <span className="h-11 w-11 rounded-full border border-[#D4AF37]/50 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37]/15 transition-colors">
                <ArrowRight className="h-5 w-5" />
              </span>
            </div>
            <div className="relative mt-8">
              <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white group-hover:text-[#E5C158] transition-colors">Topics</h2>
              <p className="mt-2 text-sm text-[#94A3B8] max-w-sm leading-relaxed">Broadcast updates to your clients.</p>
            </div>
          </Link>

          <Link
            to="/ib/activity"
            data-testid="ib-my-activity-link"
            className="card-lux card-lux-hover relative overflow-hidden p-8 flex flex-col justify-between group min-h-[240px]"
          >
            <div className="relative flex items-start justify-between">
              <span className="h-14 w-14 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-[#D4AF37]" />
              </span>
              <span className="h-11 w-11 rounded-full border border-[#D4AF37]/40 flex items-center justify-center text-[#D4AF37] group-hover:bg-[#D4AF37]/10 transition-colors">
                <ArrowRight className="h-5 w-5" />
              </span>
            </div>
            <div className="relative mt-8">
              <h2 className="font-heading text-2xl lg:text-3xl font-bold text-white group-hover:text-[#E5C158] transition-colors">My Activity</h2>
              <p className="mt-2 text-sm text-[#94A3B8] max-w-sm leading-relaxed">View your performance, referrals and network growth insights.</p>
            </div>
          </Link>
        </div>

        {/* Row B: compact Referral */}
        <div className="card-lux px-6 py-4 flex items-center gap-x-6 gap-y-3 flex-wrap" data-testid="ib-referral-card">
          <div className="flex items-center gap-2.5 shrink-0">
            <Link2 className="h-5 w-5 text-[#D4AF37]" />
            <h2 className="font-heading text-sm font-semibold text-white whitespace-nowrap">Your Referral Code</h2>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <p className="font-mono text-xl font-semibold text-gold-gradient tracking-widest" data-testid="ib-referral-code-display">{profile?.referral_code || "—"}</p>
            {profile && <CopyButton text={profile.referral_code} testId="ib-referral-code-copy-button" />}
          </div>
          <div className="hidden lg:block h-6 w-px bg-[#222530]" />
          <div className="flex items-center gap-3 flex-1 min-w-[280px]">
            <span className="text-xs text-[#64748B] tracking-wide whitespace-nowrap shrink-0">Referral Link</span>
            <p className="font-mono text-xs text-[#94A3B8] bg-[#0B0C10] border border-[#222530] rounded-lg px-4 py-2.5 truncate flex-1 min-w-0" data-testid="ib-referral-link-display">{profile?.referral_link || "—"}</p>
            {profile && <CopyButton text={profile.referral_link} testId="ib-referral-link-copy-button" label="Copy Link" />}
          </div>
        </div>

        {/* Row C: IB Wallet · Total Clients · Active Clients · My Clients */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="card-lux p-6 flex flex-col justify-between" data-testid="ib-wallet-card">
            <div className="flex items-center gap-3">
              <Wallet className="h-5 w-5 text-[#D4AF37]" />
              <div>
                <p className="text-sm font-semibold text-white leading-tight">IB Wallet</p>
                <p className="text-xs text-[#64748B]">Wallet Balance</p>
              </div>
            </div>
            <p className="font-mono text-2xl font-semibold text-gold-gradient mt-4" data-testid="ib-wallet-balance-display">
              {wallet?.available_balance != null ? `$${Number(wallet.available_balance).toFixed(2)}` : "—"}
            </p>
          </div>
          <div className="card-lux p-6 flex flex-col justify-between" data-testid="ib-total-clients-card">
            <div className="flex items-center gap-3">
              <Users className="h-5 w-5 text-[#94A3B8]" />
              <p className="text-sm text-[#64748B] tracking-wide">Total Clients</p>
            </div>
            <p className="font-mono text-3xl font-semibold text-white mt-4" data-testid="ib-total-clients-count">{profile?.total_clients ?? "—"}</p>
          </div>
          <div className="card-lux p-6 flex flex-col justify-between" data-testid="ib-active-clients-card">
            <div className="flex items-center gap-3">
              <UserCheck className="h-5 w-5 text-emerald-400" />
              <p className="text-sm text-[#64748B] tracking-wide">Active Clients</p>
            </div>
            <p className="font-mono text-3xl font-semibold text-emerald-400 mt-4" data-testid="ib-active-clients-count">{profile?.active_clients ?? "—"}</p>
          </div>
          <Link to="/ib/clients" className="card-lux card-lux-hover p-6 flex flex-col justify-between group" data-testid="ib-view-clients-link">
            <div className="flex items-center justify-between">
              <Users className="h-5 w-5 text-[#D4AF37]" />
              <span className="text-[#D4AF37] text-sm">→</span>
            </div>
            <div className="mt-4">
              <p className="text-sm font-semibold text-white group-hover:text-[#E5C158] transition-colors">My Clients</p>
              <p className="text-xs text-[#64748B]">View and manage your network</p>
            </div>
          </Link>
        </div>
      </div>

      {/* ============ MOBILE (<768px) — approved IB mobile hierarchy ============ */}
      <div className="md:hidden space-y-6" data-testid="ib-dashboard-mobile">
        {/* 2. My Activity + Topics — compact two-column primary actions (strong gold) */}
        <div className="grid grid-cols-2 gap-4">
          <Link
            to="/ib/activity"
            data-testid="ib-my-activity-link-m"
            style={{ borderColor: "rgba(212,175,55,0.7)" }}
            className="card-lux relative overflow-hidden p-4 flex flex-col border min-h-[190px]"
          >
            <div className="relative flex items-start justify-between">
              <span className="h-11 w-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/45 flex items-center justify-center">
                <BarChart3 className="h-5 w-5 text-[#D4AF37]" />
              </span>
              <ArrowRight className="h-4 w-4 text-[#D4AF37] mt-1" />
            </div>
            <div className="relative mt-3">
              <h2 className="font-heading text-lg font-bold text-white leading-tight">My Activity</h2>
              <p className="mt-1 text-[11px] text-[#94A3B8] leading-snug">View your performance, referrals and network growth insights.</p>
            </div>
          </Link>

          <Link
            to="/ib/topics"
            data-testid="ib-topics-link-m"
            style={{ borderColor: "rgba(212,175,55,0.7)" }}
            className="card-lux relative overflow-hidden p-4 flex flex-col border min-h-[190px]"
          >
            <div className="relative flex items-start justify-between">
              <span className="h-11 w-11 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/55 flex items-center justify-center">
                <Send className="h-5 w-5 text-[#D4AF37]" />
              </span>
              <ArrowRight className="h-4 w-4 text-[#D4AF37] mt-1" />
            </div>
            <div className="relative mt-3">
              <h2 className="font-heading text-lg font-bold text-white leading-tight">Topics</h2>
              <p className="mt-1 text-[11px] text-[#94A3B8] leading-snug">Broadcast updates to your clients.</p>
            </div>
          </Link>
        </div>

        {/* 3. Compact IB Wallet */}
        <Link
          to="/ib/wallet"
          data-testid="ib-wallet-card-m"
          className="card-lux ibm-wallet-glow relative overflow-hidden p-5 flex items-center gap-4"
        >
          <span className="h-12 w-12 shrink-0 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/40 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-[#D4AF37]" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-white leading-tight">IB Wallet</p>
            <p className="text-xs text-[#64748B]">Wallet Balance</p>
            <p className="font-mono text-2xl font-semibold text-gold-gradient mt-1" data-testid="ib-wallet-balance-display-m">
              {wallet?.available_balance != null ? `$${Number(wallet.available_balance).toFixed(2)}` : "—"}
            </p>
          </div>
          <ArrowRight className="h-5 w-5 text-[#D4AF37] shrink-0" />
        </Link>

        {/* 4. Compact statistics — Total + Active clients only */}
        <div className="grid grid-cols-2 gap-4">
          <div className="card-lux p-4" data-testid="ib-total-clients-card-m">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[#94A3B8]" />
              <p className="text-xs text-[#64748B] tracking-wide">Total Clients</p>
            </div>
            <p className="font-mono text-2xl font-semibold text-white mt-2" data-testid="ib-total-clients-count-m">{profile?.total_clients ?? "—"}</p>
          </div>
          <div className="card-lux p-4" data-testid="ib-active-clients-card-m">
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-emerald-400" />
              <p className="text-xs text-[#64748B] tracking-wide">Active Clients</p>
            </div>
            <p className="font-mono text-2xl font-semibold text-emerald-400 mt-2" data-testid="ib-active-clients-count-m">{profile?.active_clients ?? "—"}</p>
          </div>
        </div>

        {/* 5. Referral Code + Referral Link — compact */}
        <div className="card-lux p-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 shrink-0 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Link2 className="h-5 w-5 text-[#D4AF37]" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-[#64748B] tracking-wide">Your Referral Code</p>
              <p className="font-mono text-lg font-semibold text-gold-gradient tracking-widest truncate" data-testid="ib-referral-code-display-m">
                {profile?.referral_code || "—"}
              </p>
            </div>
            {profile && <CopyButton text={profile.referral_code} testId="ib-referral-code-copy-button-m" />}
          </div>
          <div className="border-t border-[#222530] pt-4">
            <p className="text-xs text-[#64748B] mb-2 tracking-wide">Referral Link</p>
            <div className="flex items-center gap-2">
              <p className="font-mono text-[11px] text-[#94A3B8] bg-[#0B0C10] border border-[#222530] rounded-lg px-3 py-2.5 truncate flex-1 min-w-0" data-testid="ib-referral-link-display-m">
                {profile?.referral_link || "—"}
              </p>
              {profile && <CopyButton text={profile.referral_link} testId="ib-referral-link-copy-button-m" label="Copy Link" />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
